import { useEffect, useRef, useState } from 'react';
import { refreshAll } from '../lib/query.ts';
import { toast } from './ui.tsx';

const THRESHOLD = 72;
const MAX = 120;
/** the finger moves twice as far as the content: the pull feels like stretching, not scrolling */
const resist = (dy: number) => Math.min(MAX, dy * 0.5);

/**
 * Pull the page down from the very top to reload everything on screen (phones). The content
 * follows the finger; past the threshold the arrow turns into a spinner and the page stays
 * lowered until the data has arrived.
 */
export function PullToRefresh({ target }: { target: React.RefObject<HTMLElement | null> }) {
  const [pull, setPull] = useState(0);
  const [busy, setBusy] = useState(false);
  const state = useRef({ startY: 0, startX: 0, active: false, dy: 0, busy: false });

  useEffect(() => {
    const s = state.current;
    const canStart = () => !s.busy && window.scrollY <= 0 && !document.querySelector('dialog[open]');
    const start = (e: TouchEvent) => {
      if (e.touches.length !== 1 || !canStart()) return;
      s.startY = e.touches[0].clientY;
      s.startX = e.touches[0].clientX;
      s.active = true;
      s.dy = 0;
    };
    const move = (e: TouchEvent) => {
      if (!s.active) return;
      const dy = e.touches[0].clientY - s.startY;
      const dx = Math.abs(e.touches[0].clientX - s.startX);
      if (dy <= 0 || dx > dy || window.scrollY > 0) {
        if (s.dy) { s.dy = 0; setPull(0); }
        if (dy < 0 || dx > dy) s.active = false;
        return;
      }
      e.preventDefault();
      s.dy = resist(dy);
      setPull(s.dy);
    };
    const end = async () => {
      if (!s.active) return;
      s.active = false;
      if (s.dy < THRESHOLD) { s.dy = 0; setPull(0); return; }
      s.busy = true;
      setBusy(true);
      setPull(THRESHOLD * 0.75);
      navigator.vibrate?.(8);
      try {
        // the spinner stays at least a moment so a fast answer still reads as «updated»
        await Promise.all([refreshAll(), new Promise((r) => setTimeout(r, 450))]);
      } catch (e) {
        toast((e as Error).message, 'error');
      }
      s.busy = false;
      s.dy = 0;
      setBusy(false);
      setPull(0);
    };
    window.addEventListener('touchstart', start, { passive: true });
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('touchend', end);
    window.addEventListener('touchcancel', end);
    return () => {
      window.removeEventListener('touchstart', start);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', end);
      window.removeEventListener('touchcancel', end);
    };
  }, []);

  useEffect(() => {
    const el = target.current;
    if (!el) return;
    el.style.setProperty('--pull', `${pull}px`);
    el.classList.toggle('pulling', pull > 0 && !busy && state.current.active);
  }, [pull, busy, target]);

  const progress = Math.min(1, pull / THRESHOLD);
  return (
    <div className={`ptr${busy ? ' ptr-busy' : ''}${pull > 0 ? ' ptr-on' : ''}${!busy && pull >= THRESHOLD ? ' ptr-ready' : ''}`} style={{ '--ptr-y': `${pull}px`, '--ptr-p': progress } as React.CSSProperties}
      role="status" aria-live="polite">
      <span className="ptr-mark" aria-hidden>
        {busy ? <span className="spinner" /> : (
          <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5v14M6 13l6 6 6-6" />
          </svg>
        )}
      </span>
      <span className="sr-only">{busy ? 'Обновляю данные' : ''}</span>
    </div>
  );
}
